import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscriber, catchError, map, of, tap } from 'rxjs';
import { APIResponse } from '../models/apiResponse';
import { HttpClient } from '@angular/common/http';
import { NotificationModel } from '../models/notification';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { environment } from '../../../environments/environment';
import { io, Socket } from 'socket.io-client';
import { OcadoAuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends Subject<DataStateChangeEventArgs> {
  public dashboardBaseUrl = '';
  public uploadDownloadBaseUrl = '';
  public baseUrl = '';
  public socketPath = '';
  private notificationList!: BehaviorSubject<NotificationModel[]>;
  public notificationCommentsTab = new BehaviorSubject<number>(0);
  public notificationCount = new BehaviorSubject<number>(0);
  public filterNotificationData = new BehaviorSubject<any[]>([]);
  private eventSource!: EventSource;
  private socket!: Socket;

  private dataStore: {
    notificationData: any[];
  };

  constructor(private http: HttpClient, private zone: NgZone, private ocadoAuthService: OcadoAuthService) {
    super();
    this.dashboardBaseUrl = environment.dashboardBaseUrl;
    this.uploadDownloadBaseUrl = environment.uploadDownloadBaseUrl;
    this.baseUrl = environment.baseUrl;
    this.socketPath = environment.socketPath;
    this.dataStore = { notificationData: [] };
    this.notificationList = new BehaviorSubject<NotificationModel[]>([]);
  }

  fetchNotificationData(value: string, pageNumber: number, pageSize: number) {
    const url = this.dashboardBaseUrl + 'notification/get-messages?value=' + value + '&page_number=' + pageNumber + '&page_size=' + pageSize + '';
    return this.http.get<APIResponse>(url).subscribe((data: APIResponse) => {
      this.dataStore.notificationData = data.result;
      this.notificationList.next(Object.assign({}, this.dataStore).notificationData);
      if (data?.result?.count !== undefined) {
        this.notificationCount.next(data.result.count);
      }
    });
  }

  public execute(state: any, value: string, pageNumber: number, pageSize: number) {
    return this.getNotificationData(state, value, pageNumber, pageSize).subscribe(data => super.next(data));
  }

  getNotificationData(state: DataStateChangeEventArgs, value: string, pageNumber: number, pageSize: number): Observable<DataStateChangeEventArgs> {
    const url = this.dashboardBaseUrl + 'notification/get-messages?value=' + value + '&page_number=' + pageNumber + '&page_size=' + pageSize + '';
    return this.http.get<APIResponse>(url).pipe(map((response: any) => response.result))
      .pipe(map((response: any) => (<any> {
        result: response.rows,
        count: response.count,
        // here we are returning the data as Object of result and count 
      })))
      .pipe((data: any) => data);
  }

  fetchUnreadCount() {
    const url = this.dashboardBaseUrl + 'notification/get-messages?value=N&page_number=1&page_size=10';
    return this.http.get(url).pipe(
      tap((data: any) => {
        if (data?.result?.count !== undefined) {
          this.notificationCount.next(data.result.count);
        }
      })
    );
  }

  readNotificationData(id: any) {
    const url = this.dashboardBaseUrl + 'notification?id=' + id + '';
    return this.http.get<APIResponse>(url);
  }

  setupSocketConnection(): void {
    // Offload notification badge count to REST get-messages call instead of persistent WebSocket
    this.fetchUnreadCount().subscribe({
      next: (data: any) => {
        if (data?.result?.count !== undefined) {
          this.notificationCount.next(data.result.count);
        }
      },
      error: () => {
        // Silently catch if not yet authenticated
      }
    });

    // Socket.IO commented out per architectural simplification; bell count offloaded to get-messages
    /*
    const app_token = this.ocadoAuthService.appToken;
    const user_uid = this.ocadoAuthService.userUid;
    this.socket = io(this.baseUrl, {
      path: this.socketPath,
      reconnection: true,
      autoConnect: false,
      transports: [ "websocket" ],
      auth: {
        "Access_token": app_token
      }
    });
    this.socket.on('count_' + user_uid, (data: number) => {
      this.notificationCount.next(data);
    });
    this.socket.connect();
    */
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  getEventSource() {
    const app_token = this.ocadoAuthService.appToken;
    const url = this.dashboardBaseUrl + 'notification/count-sse?authorization=' + app_token + '';
    return new EventSource(url);
  }

  connectToServerSentEvents(eventNames: string[] = []): Observable<Event> {
    this.eventSource = this.getEventSource();

    return new Observable((subscriber: Subscriber<Event>) => {
      eventNames.forEach((event: string) => {
        this.eventSource.addEventListener(event, (data: any) => {
          this.zone.run(() => subscriber.next(data));
        });
      });
    });
  }


  updateNotificationCount(currentDateTime: any): Observable<any> {
    const url = this.dashboardBaseUrl + 'notification/update-count';
    return this.http.post<APIResponse>(url, currentDateTime);
  }

  public downloadNotificationCsv(entity_id: number): Observable<any> {
    const url = `${this.uploadDownloadBaseUrl}excel/download-async-csv-promotion?id=${entity_id}`;
    return this.http.get<APIResponse>(url).pipe(catchError((err) => {
      return of({ result: {} as any, code: err.error.error });
    }));
  }

  get notificationData() {
    return this.notificationList;
  }

  close(): void {
    if (!this.eventSource) {
      return;
    }

    this.eventSource.close();
  }
}
